import type { AnalyticsEventMap } from '@cowbell/data/analytics';
import {
  LayoutContainer,
  MooChat,
  MooChatProvider,
  useAgentsCatalog,
  useShellAgents,
  useShellDrawer,
  useShellLauncher,
  useShellLayout,
  useShellPane,
  useShellSelection,
  useShellStorageHydrated,
  useMooConfig,
  type A2UIClientAction,
  type AnalyticsAdapter,
  type MooChatConfig,
  type NavigationAdapter,
  type RenderAgentAvatar,
  type RenderStarterIcon,
  type RenderUserAvatar,
} from '@cowbell/moo';
import { rootFontPx } from '@cowbell/ui';
import { cowbellV1Catalog } from '@cowbell/ui/components/ai/catalog';
import { ConnectedAppsBridge } from './ConnectedAppsBridge';
import { Avatar, Group, Show, Text } from '@cowbell/ui/core';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faCircleQuestion,
  faCompass,
  faFileLines,
  faListCheck,
  faMagnifyingGlass,
  faShieldHalved,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useNotifications } from '../../../_lib/notifications/useNotifications';
import { CmailModal } from '../../../admin/remittances/CmailModal';
import { useAnalytics } from '../../../config/analytics/useAnalytics';
import { useActor } from '../../hooks/useActor';
import { useAuth } from '../../hooks/useAuth';
import { useScopes } from '../../hooks/useScopes';
import { UserAvatar, useStaffAvatarManifest } from '../../user-avatar';
import AgentAvatar from '../components/AgentAvatar';
import { AskMooHeader } from './AskMooHeader';
import { getEntityConfig } from '../components/context-chips/entityConfig';
import ActiveRunCountBadge from '../components/sidebars/ActiveRunCountBadge';
import ComparablesPanel from '../components/sources/ComparablesPanel';
import FilePreviewPanel from '../components/sources/FilePreviewPanel';
import JsonViewerPanel from '../components/sources/JsonViewerPanel';
import FindingsPanel from '../components/sources/FindingsPanel';
import PDFViewerPanel from '../components/sources/PDFViewerPanel';
import { PolicyOverlayDrawer } from '../components/sources/PolicyOverlayDrawer';
import { QuoteOverlayDrawer } from '../components/sources/QuoteOverlayDrawer';
import {
  buildMooPolicySource,
  buildPoliciesListPath,
  buildQuoteReviewPath,
  buildSourceUrl,
  handleSourceClick,
  stripMooPolicyParam,
} from '../components/sources/SourcePreviewHandler';
import CvePanel from '../components/sources/CvePanel';
import ThreatActorPanel from '../components/sources/ThreatActorPanel';
import { AskMooContext } from '../context/AskMooProvider';
import {
  normalizeConversationId,
  useActiveAsyncRuns,
} from '../hooks/useActiveAsyncRuns';
import { EMPTY_AGENT_TONES, useAgentTones } from '../hooks/useAgentTones';
import { useAllowedAgentIds } from '../hooks/useAllowedAgentIds';
import { useHiddenAgentIds } from '../hooks/useHiddenAgentIds';
import type {
  Agent,
  ComparablesMetadata,
  FindingsMetadata,
  Source,
  Agent as V1Agent,
} from '../types';
import {
  canUseMooStudio,
  hasRestrictedCatalog,
  restrictedFailsafeAgentId,
} from '../utils/ask-moo-access';
import { mapPersonaToUserRole } from '../utils/role-mapping';
import { downloadArtifact } from './artifact-download';
import { artifactAsSource, resolveArtifactRef } from './artifact-preview-url';
import { downloadArtifactsAsZip } from './artifact-zip';
import { MooChatFloatingIcon } from './components/MooChatFloatingIcon';
import { makeCowbellChatTransport } from './cowbell-chat-transport';
import { makeCowbellStorageAdapter } from './cowbell-storage-adapter';
import { makeCowbellTransportRest } from './cowbell-transport-rest';
import { formatEmailForClipboard } from './format-email-for-clipboard';
import { linkQuoteInMessage } from './quote-result-message';
import { queryKeysToRefreshAfterTurn } from './refresh-after-turn';
import {
  useUpdateUserPreferences,
  useUserPreferences,
} from './hooks/useUserPreferences';
import {
  MOO_V2_VOICE_CANCEL_EVENT,
  MOO_V2_VOICE_COMMIT_EVENT,
  useVoiceInput,
} from './hooks/useVoiceInput';
import { makeActionCardRenderers } from './plugins/action-card-renderers';
import { consoleLoggerAdapter } from './plugins/logger-adapter';
import {
  handleViewSurfaceAction,
  isViewSurfaceAction,
} from './view-surface-actions';
import { renderConversationAccessory } from './plugins/render-conversation-accessory';
import { useDerivedScreenContext } from './route-context-source';
import {
  consumeAskMooOpenOnMount,
  currentLocationPath,
  rememberStudioReturnPath,
} from './studio-return';
import { MOO_V2_OPEN_SOURCE_PANEL_EVENT } from './source-panel-events';
import { MOO_SIDEBAR_WIDTH_CSS_VAR } from './sidebar-contract';
import { publishLauncherStatus } from './launcher-status-store';
import { isLauncherShortcut } from './launcher-shortcut';

export const MOO_V2_TOGGLE_EVENT = 'moo:v2-toggle';
/** Window event to deep-link the Moo panel to a conversation. Chat state lives
 *  in @cowbell/moo's local provider, unreachable from a global snackbar, so the
 *  nudge cards signal through the window instead. */
export const MOO_V2_OPEN_CONVERSATION_EVENT = 'moo:v2-open-conversation';
export const MOO_V2_OPEN_THREAT_ACTOR_EVENT = 'moo:v2-open-threat-actor';
export const MOO_V2_OPEN_CVE_EVENT = 'moo:v2-open-cve';
// Re-exported for existing importers; declared in its own module so leaf
// components can dispatch it without pulling this file's heavy dependencies.
export { MOO_V2_OPEN_SOURCE_PANEL_EVENT };
const MOO_STUDIO_ROUTE = '/moo/studio/agents';

type ActivePanel =
  | { type: 'pdf'; source: Source }
  | { type: 'filePreview'; source: Source }
  | { type: 'comparables'; source: Source }
  | { type: 'findings'; source: Source }
  | { type: 'policy'; source: Source }
  | { type: 'quote'; source: Source }
  | { type: 'cmail'; source: Source }
  | { type: 'json'; data: unknown };
interface SourceDetailRowProps {
  readonly label: string;
  readonly value: number | string;
}
export const SIDEBAR_STORAGE_KEY = 'moo.v2.sidebarWidth';
const SIDEBAR_DEFAULT_WIDTH = 420;
/** The PDF preview opens at 64rem, the 768px it had before it moved onto the shared panel shell, at the console's 12px root. */
const PDF_PANEL_DEFAULT_WIDTH_REM = 64;

/** Read the current sidebar width from the ResizableSidebar's CSS custom property
 *  on the document root. Falls back to the persisted localStorage value (written
 *  by ResizableSidebar on resize commit), then to the component default (420px).
 *  The CSS var is set while the sidebar is open and removed on close — a 0 result
 *  means the sidebar is closed; the panel's own visibility gating handles that. */
function readSidebarWidth(): number {
  if (typeof document === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  const cssVal = document.documentElement.style
    .getPropertyValue(MOO_SIDEBAR_WIDTH_CSS_VAR)
    .trim();
  if (cssVal) {
    const parsed = Number.parseInt(cssVal, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) {
      const parsed = Number(stored);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    /* localStorage unavailable */
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

function SourceDetailRow({ label, value }: SourceDetailRowProps) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

function renderComparablesSourceBody(source: Source) {
  const metadata = source.metadata as ComparablesMetadata;
  return (
    <>
      <SourceDetailRow label="Account" value={metadata.accountName} />
      <SourceDetailRow label="Quote" value={metadata.quoteNumber} />
      <SourceDetailRow label="NAICS" value={metadata.naicsCode} />
      <SourceDetailRow label="Revenue" value={metadata.revenue} />
      <SourceDetailRow label="Limit" value={metadata.limit} />
      <SourceDetailRow
        label="Current premium"
        value={metadata.currentPremium}
      />
      <SourceDetailRow label="Effective date" value={metadata.effectiveDate} />
      {metadata.comparables.map((comparableSource) => (
        <Group
          gap="xs"
          align="center"
          key={`${comparableSource.accountName}-${comparableSource.naicsCode}`}
        >
          <Text size="sm" fw={600}>
            {comparableSource.accountName}
          </Text>
          <SourceDetailRow label="NAICS" value={comparableSource.naicsCode} />
          <SourceDetailRow label="Revenue" value={comparableSource.revenue} />
          <SourceDetailRow label="Limit" value={comparableSource.limit} />
          <SourceDetailRow label="Premium" value={comparableSource.premium} />
          <SourceDetailRow
            label="Cowbell score"
            value={comparableSource.cowbellScore}
          />
        </Group>
      ))}
    </>
  );
}

function renderFindingsSourceBody(source: Source) {
  const metadata = source.metadata as FindingsMetadata;
  return (
    <>
      <SourceDetailRow label="Account" value={metadata.accountName} />
      <SourceDetailRow label="Account ID" value={metadata.accountId} />
    </>
  );
}

/** Tracks the ResizableSidebar width live via a MutationObserver on the root
 *  element's style attribute. Initialized from localStorage / CSS var so the
 *  panel never starts at 0 even before the sidebar opens. */
function useSidebarWidth(): number {
  const [sidebarWidth, setSidebarWidth] = useState<number>(readSidebarWidth);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    observerRef.current = new MutationObserver(() => {
      setSidebarWidth(readSidebarWidth());
    });
    observerRef.current.observe(root, {
      attributes: true,
      attributeFilter: ['style'],
    });
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return sidebarWidth;
}

// Module-level reference so the array identity is stable across re-renders.
// A2UISurfaceList lists this in its MessageProcessor useMemo deps; a fresh
// `[cowbellV1Catalog]` literal every render recreates the processor and
// replays envelopes, which would re-run updateDataModel and wipe whatever
// the user has typed into an open EmailComposer.
const A2UI_CATALOGS = [cowbellV1Catalog];

/** Library uses semantic 'sm'|'md'|'lg' for its slot calls. Only shrink
 *  the chat-row size (library 'sm' → V1 'xs' = 24px) so the assistant
 *  avatar matches the user avatar in message rows. The drawer ('md') and
 *  empty-state hero ('lg') stay at V1's native pixel scale. */
type SlotSize = 'sm' | 'md' | 'lg';

// md is the drawer's agent list: a tile as tall as the name and description
// beside it.
const AGENT_AVATAR_SIZE_MAP: Record<SlotSize, 'xs' | 'sm' | 'lg'> = {
  sm: 'xs',
  md: 'sm',
  lg: 'lg',
};

const renderAgentAvatar: RenderAgentAvatar = (agent, { size }) => (
  <AgentAvatar
    agent={
      agent as Pick<V1Agent, 'id' | 'name' | 'icon' | 'color' | 'category'>
    }
    size={AGENT_AVATAR_SIZE_MAP[size]}
  />
);

/** Resolves the current actor's initials from Redux + renders a Mantine
 *  Avatar. Defined as a real component so it can call `useActor` — the
 *  config slot passes the size through, and React renders this each time
 *  the actor or persona changes. */
function HostUserAvatar({ size }: Readonly<{ size: SlotSize }>) {
  const actor = useActor();
  const manifest = useStaffAvatarManifest();
  // Pixel box for both branches; see the comment on USER_AVATAR_PX below.
  const px = USER_AVATAR_PX[size];
  if (manifest) {
    const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ');
    return (
      <span
        style={{
          display: 'inline-flex',
          width: px,
          height: px,
          minWidth: px,
          minHeight: px,
          flexShrink: 0,
          fontSize: px,
        }}
      >
        <UserAvatar
          email={actor.email}
          name={name}
          size="fill"
          label={name || actor.email || 'User avatar'}
        />
      </span>
    );
  }
  const initials = actorInitials(actor.firstName, actor.lastName);
  // V1's AgentAvatar paints an SVG at explicit pixel dimensions (sm=32px),
  // which ignores any --mantine-scale factor the host has set. Mantine's
  // numeric `size` prop *is* scaled, so the user avatar ends up smaller
  // (e.g. 32×0.75=24px) when the host uses a compressed scale. Force the
  // outer box dimensions inline so the user avatar matches the assistant.
  return (
    <Avatar
      size={px}
      radius="xl"
      color="blue"
      aria-label="User avatar"
      style={{
        width: px,
        height: px,
        minWidth: px,
        minHeight: px,
        flexShrink: 0,
      }}
    >
      {initials}
    </Avatar>
  );
}

// Match the AGENT_AVATAR_SIZE_MAP scale: sm pins to 24px like the 'xs' agent
// tile, md to 32px like the drawer's 'sm' tile, lg to the empty-state hero.
const USER_AVATAR_PX: Record<SlotSize, number> = {
  sm: 24,
  md: 32,
  lg: 46,
};

const renderUserAvatar: RenderUserAvatar = ({ size }) => (
  <HostUserAvatar size={size} />
);

function actorInitials(firstName?: string, lastName?: string): string {
  const first = (firstName ?? '').charAt(0);
  const last = (lastName ?? '').charAt(0);
  const combined = (first + last).toUpperCase();
  return combined.length > 0 ? combined : '?';
}

// Mirrors V1's ConversationStarters icon map — same set of server-side
// keys, so swapping flag values doesn't require backend coordination.
const STARTER_ICON_MAP: Record<string, IconDefinition> = {
  analyze: faBolt,
  risk: faShieldHalved,
  coverage: faFileLines,
  document: faFileLines,
  search: faMagnifyingGlass,
  task: faListCheck,
  help: faCircleQuestion,
  question: faCircleQuestion,
  discover: faCompass,
};

const renderStarterIcon: RenderStarterIcon = (icon, { size }) => {
  const def = STARTER_ICON_MAP[icon] ?? faCircleQuestion;
  return <FontAwesomeIcon icon={def} style={{ width: size, height: size }} />;
};

export function AskMooIntegrationV2() {
  const { accountType } = useAuth();
  const role = mapPersonaToUserRole(accountType);
  const history = useHistory();
  const actor = useActor();
  const { firstName } = actor;
  // Moo Studio is Cowbell-only. `/moo/studio` already redirects non-Cowbell
  // (MooRoutesWrapper), so hide the shell's "Open Moo Studio" entry too rather
  // than surface an affordance that dead-ends in that redirect.
  const canOpenStudio = canUseMooStudio(actor);
  const { track } = useAnalytics();
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const voiceInput = useVoiceInput();

  // Provides the dropdown list for the library's tone selector.
  // Active tone selection lives in ChatStream state (starts null each session).
  // Auto-select-default (AgentTone.isDefault) is deferred — not yet wired in V2.
  const { data: agentTones = EMPTY_AGENT_TONES } = useAgentTones();
  const tones = useMemo(
    () => agentTones.map((t) => ({ id: t.id, name: t.name })),
    [agentTones]
  );

  const analytics = useMemo<AnalyticsAdapter>(
    () => ({
      track: (event, props) => {
        // The library's AnalyticsAdapter takes a plain string; the host's
        // useAnalytics is keyed by AnalyticsEventMap. All MooAnalyticsEvents
        // values are intersected into AnalyticsEventMap (see libs/ui-data
        // moo.events.ts), so the cast is safe; useAnalytics injects actor /
        // persona / accountType automatically.
        track(event as keyof AnalyticsEventMap, (props ?? {}) as never);
      },
    }),
    [track]
  );

  const transport = useMemo(
    () => ({
      ...makeCowbellTransportRest(),
      chat: makeCowbellChatTransport(role),
    }),
    [role]
  );

  // `limit_moo_agents`: narrows the panel's agent catalog to an allow-list of
  // agent ids. null when the flag isn't limiting anything. The library applies
  // it when it seeds the shell catalog, so the Agents section, "Browse all
  // agents" and the default-agent pick stay consistent with one another.
  const allowedAgentIds = useAllowedAgentIds();
  // Agents still behind a feature flag (today `unified_uw_agent`), removed
  // from the catalog the same way.
  const hiddenAgentIds = useHiddenAgentIds();

  // Stable identity, not per-render — makeCowbellStorageAdapter() takes no
  // args and its sessionStorage/localStorage keys never change, but
  // useStorageHydration's Phase 2a effect (in @cowbell/moo) keys off this
  // object's identity and unconditionally re-persists `selection.agentId`
  // whenever it changes. Building it fresh inside the `config` memo below —
  // which recomputes on every render because `voiceInput` (from
  // useVoiceInput()) is a new object every render — meant any direct write
  // to the adapter (e.g. clearing it on unpin, OMNI-670) got silently
  // overwritten by that effect on the very next render. Bugbot caught this.
  const storageAdapter = useMemo(() => makeCowbellStorageAdapter(), []);

  // Stable handler identity. A2UISurfaceList puts this in its MessageProcessor
  // useMemo deps; a fresh inline arrow every render would recreate the
  // processor mid-edit and reset the surface data model, wiping user input.
  const onA2UIAction = useCallback(
    async (action: A2UIClientAction) => {
      // Returns before the transport call: these change the view the user is
      // looking at, not the stored record, so they are never POSTed.
      if (isViewSurfaceAction(action.name)) {
        return handleViewSurfaceAction(action);
      }

      if (action.name === 'Copy') {
        const text = formatEmailForClipboard(action.payload);
        try {
          await navigator.clipboard.writeText(text);
          notifications.success('Copied to clipboard');
        } catch {
          notifications.error("Couldn't copy to clipboard");
        }
        return;
      }
      // Pure local navigation, exactly like V1's QuoteDraftCard — never a
      // backend action. The agent binds newQuoteId/product via `context`
      // paths pointing at `/results/<optionId>` (populated by the
      // GenerateQuoteOption branch below once a quote exists), so both are
      // already resolved into `action.payload` by the time this fires.
      if (action.name === 'OpenQuoteReview') {
        const newQuoteId =
          typeof action.payload.newQuoteId === 'string'
            ? action.payload.newQuoteId
            : '';
        const product =
          typeof action.payload.product === 'string'
            ? action.payload.product
            : '';
        if (newQuoteId) {
          history.push(buildQuoteReviewPath(newQuoteId, product, role));
        }
        return;
      }
      // Client-only, like Copy: the agent binds the artifact's downloadUrl and
      // filename into this action's context. The authed fetch lives in the
      // console (session token, proxy rewrite); we catch failures here so they
      // surface a toast instead of silently stopping the card's spinner.
      if (action.name === 'DownloadArtifact') {
        const downloadUrl =
          typeof action.payload.downloadUrl === 'string'
            ? action.payload.downloadUrl
            : '';
        const filename =
          typeof action.payload.filename === 'string'
            ? action.payload.filename
            : '';
        if (downloadUrl) {
          try {
            await downloadArtifact(downloadUrl, filename);
          } catch {
            notifications.error("Couldn't download file");
          }
        }
        return;
      }
      // Sequential, not parallel: each presign is awaited before the next
      // anchor click, because several navigations fired at once trip popup
      // blockers and silently drop files. One toast for the whole batch.
      if (action.name === 'DownloadAllArtifacts') {
        const files = Array.isArray(action.payload.files)
          ? action.payload.files
          : [];
        if (files.length === 0) return;
        // One archive, not N downloads: Chrome gates several simultaneous
        // downloads behind a "Download multiple files?" prompt and silently
        // drops the rest if it's declined.
        try {
          const { failed } = await downloadArtifactsAsZip(files);
          if (failed > 0) {
            notifications.error(
              failed === 1
                ? "1 file couldn't be added to the archive"
                : `${failed} files couldn't be added to the archive`
            );
          }
        } catch {
          // The archive path can fail where individual downloads succeed: it
          // buffers everything in memory (so a large batch aborts) and reads
          // bytes off /download, whereas this fallback fetches /presign and
          // hands the URL to an iframe. So a failure that kills every read
          // above — CORS on the S3 redirect, for one — can leave this path
          // working. One download per file, each in its own iframe, since
          // successive navigations in a single frame cancel each other.
          let failed = 0;
          for (const file of files) {
            const { downloadUrl, filename } = (file ?? {}) as {
              downloadUrl?: unknown;
              filename?: unknown;
            };
            if (typeof downloadUrl !== 'string' || !downloadUrl) continue;
            try {
              await downloadArtifact(
                downloadUrl,
                typeof filename === 'string' ? filename : '',
                { batched: true }
              );
            } catch {
              failed += 1;
            }
          }
          if (failed > 0) {
            notifications.error(
              failed === 1
                ? "Couldn't download 1 file"
                : `Couldn't download ${failed} files`
            );
          }
        }
        return;
      }
      // Client-only, like DownloadArtifact: opens the existing right-docked
      // preview panel on a generated file. The artifact is adapted into the
      // `Source` shape both panels already consume, so neither needed changing —
      // PDFs keep going to PDFViewerPanel, everything else to FilePreviewPanel,
      // which resolves the renderer by extension. setActivePanel lives in
      // `Inner`, so this hops through the same window event AsyncTaskProgress
      // uses to open a panel from deep inside a message.
      if (action.name === 'PreviewArtifact') {
        const ref = resolveArtifactRef(action.payload);
        if (!ref) return;
        const source = artifactAsSource(ref);
        // PDFs have their own viewer panel; everything else goes to the file
        // preview panel, which resolves its renderer by extension.
        const isPdf = (ref.filename ?? '').toLowerCase().endsWith('.pdf');
        globalThis.dispatchEvent(
          new CustomEvent(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
            detail: { type: isPdf ? 'pdf' : 'filePreview', source },
          })
        );
        return;
      }
      const result = await transport.executeSurfaceAction({
        correlationId: action.correlationId,
        messageId: action.messageId,
        surfaceId: action.surfaceId,
        surfaceType: action.surfaceType,
        sourceComponentId: action.sourceComponentId,
        action: action.name,
        payload: action.payload,
      });
      if (!result.ok) {
        notifications.error(`Couldn't complete ${action.name}`);
      }
      if (action.name === 'SaveQuoteEdit' && result.ok) {
        const quoteId =
          typeof action.payload.quoteId === 'string'
            ? action.payload.quoteId
            : '';
        const { pathname } = history.location;
        if (
          quoteId &&
          pathname.includes(quoteId) &&
          pathname.includes('/edit')
        ) {
          notifications.success('Quote edited successfully.');
          history.push(
            pathname.startsWith('/agency') ? '/agency/inbox' : '/admin/quotes'
          );
        }
      }
      // Hand ok/message/data back to the library so it can append a
      // synthetic assistant turn for in-thread feedback, swap an
      // EmailComposer to its read-only "Sent" view on success (so the user
      // can't accidentally double-send), and — for GenerateQuoteOption —
      // write the new quote's id/number/premium into this component's
      // `/results/<sourceComponentId>` data-model path so the matching
      // QuoteOption re-renders with the generated state. A generated quote's
      // confirmation links to the quote.
      const message =
        action.name === 'GenerateQuoteOption' && result.ok
          ? linkQuoteInMessage(result.message, result.data, (id, product) =>
              buildQuoteReviewPath(id, product, role)
            )
          : result.message;
      return {
        ok: result.ok,
        message,
        data: result.data,
        a2uiMessages: result.a2uiMessages,
      };
    },
    [transport, notifications, history, role]
  );

  // A tool the agent ran can change data a page on screen is showing (e.g. a
  // freshly generated risk report); refresh it so the page doesn't wait for a
  // reload.
  const onTurnSucceeded = useCallback(
    ({ toolNames }: { readonly toolNames: readonly string[] }) => {
      for (const queryKey of queryKeysToRefreshAfterTurn(toolNames)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient]
  );

  const config = useMemo<MooChatConfig>(() => {
    const navigation: NavigationAdapter = {
      navigate: (to, opts) => {
        if (opts?.replace) {
          history.replace(to);
        } else {
          history.push(to);
        }
      },
      getCurrentPath: () =>
        globalThis.location === undefined ? '/' : globalThis.location.pathname,
      subscribe: () => () => undefined,
      // Omit openStudio for non-Cowbell so the library hides its "Open Moo
      // Studio" row (StudioLinkRow renders only when openStudio is supplied).
      // Studio renders outside ConsoleLayout, so this panel unmounts on the way
      // there: record the current route for Studio's "Back to Ask Moo" to
      // return to (see studio-return.ts).
      ...(canOpenStudio
        ? {
            openStudio: () => {
              rememberStudioReturnPath(currentLocationPath());
              history.push(MOO_STUDIO_ROUTE);
            },
          }
        : {}),
    };
    return {
      transport,
      analytics,
      navigation,
      logger: consoleLoggerAdapter,
      // Split storage: sessionStorage for selected agent (per-tab,
      // V1 parity); localStorage for pinned user contexts (V2
      // enhancement). See cowbell-storage-adapter for the keys. Stable
      // reference — see the storageAdapter memo above.
      storage: storageAdapter,
      renderAgentAvatar,
      renderStarterIcon,
      renderUserAvatar,
      renderConversationAccessory,
      userDisplayName: firstName,
      actionCards: makeActionCardRenderers(),
      // V1 source router stays the source of truth for routing decisions —
      // external tabs, deep-link via SPA navigate, role-aware UW vs detail
      // routes. Threat-actor citations open the in-app ThreatActorPanel via
      // a browser event (config memo has no React state; Inner owns the panel
      // state and listens for MOO_V2_OPEN_THREAT_ACTOR_EVENT).
      onSourceClick: (source) =>
        handleSourceClick(source, {
          navigate: (path) => history.push(path),
          userRole: role,
          onShowThreatActor: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<Source>(MOO_V2_OPEN_THREAT_ACTOR_EVENT, {
                detail: s,
              })
            ),
          onShowCve: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<Source>(MOO_V2_OPEN_CVE_EVENT, {
                detail: s,
              })
            ),
          onOpenPdf: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'pdf', source: s },
              })
            ),
          onOpenFilePreview: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'filePreview', source: s },
              })
            ),
          onShowComparables: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'comparables', source: s },
              })
            ),
          onShowFindings: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'findings', source: s },
              })
            ),
          onShowPolicy: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'policy', source: s },
              })
            ),
          onShowQuote: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'quote', source: s },
              })
            ),
          onShowEmail: (s) =>
            globalThis.dispatchEvent(
              new CustomEvent<ActivePanel>(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
                detail: { type: 'cmail', source: s },
              })
            ),
        }),
      getSourceUrl: (source) => buildSourceUrl(source, role),
      a2uiCatalogs: A2UI_CATALOGS,
      onA2UIAction,
      onTurnSucceeded,
      entityIconResolver: (type) => getEntityConfig(type)?.icon,
      tones,
      voiceInput,
      allowedAgentIds,
      hiddenAgentIds,
    };
  }, [
    role,
    history,
    firstName,
    canOpenStudio,
    analytics,
    transport,
    storageAdapter,
    onA2UIAction,
    onTurnSucceeded,
    tones,
    voiceInput,
    allowedAgentIds,
    hiddenAgentIds,
  ]);

  return (
    <MooChatProvider config={config}>
      <Inner />
    </MooChatProvider>
  );
}

function Inner() {
  const history = useHistory();
  // Read the context directly (not the throwing useAskMoo hook) so V2 renders
  // even in harnesses without an AskMooProvider; the deep link is absent there.
  const askMooContext = useContext(AskMooContext);
  const initialPolicyDeepLink = askMooContext?.initialPolicyDeepLink ?? null;
  const clearInitialPolicyDeepLink = askMooContext?.clearInitialPolicyDeepLink;
  const scopes = useScopes();
  const openedViaDeepLinkRef = useRef(false);
  const movedToPoliciesRef = useRef(false);
  const deepLinkLandingPathRef = useRef<string | null>(null);
  useDerivedScreenContext();
  // Kick off the agents catalog fetch as soon as the V2 integration mounts.
  // The drawer also calls this hook (TanStack dedupes), but doing it here
  // ensures `agents.defaultAgentId` is populated even before a user opens
  // the drawer — needed for the auto-select-default-agent effect below.
  useAgentsCatalog();

  // Track the live width of the ResizableSidebar so ThreatActorPanel docks
  // to its left edge (mirrors V1 / PDFViewerPanel behaviour).
  const sidebarWidth = useSidebarWidth();

  const pane = useShellPane();
  const launcher = useShellLauncher();
  const drawer = useShellDrawer();
  const layout = useShellLayout();
  const agents = useShellAgents();
  const selection = useShellSelection();
  const { data: userPrefs } = useUserPreferences();
  const updatePrefs = useUpdateUserPreferences();
  // Only used to reach into the storage adapter directly on unpin (see
  // handlePinToggle) — useStorageHydration otherwise owns all reads/writes.
  const { storage, allowedAgentIds, hiddenAgentIds } = useMooConfig();
  // Polled whether or not the panel is open: the top-bar button shows a
  // working pulse precisely when the panel is closed (one request a minute).
  const activeRunCounts = useActiveAsyncRuns();
  // Storage hydration runs in MooChatProvider; this gate stays false until
  // useStorageHydration has read the persisted agentId (if any). Gating
  // the auto-select effect on it prevents the default from overwriting a
  // session-restored agent when both effects land in the same flush.
  const storageHydrated = useShellStorageHydrated();

  // Commit a finished voice transcript into the composer. Dispatched by
  // useVoiceInput whenever a recording session ends with non-empty text.
  const composerTextRef = useRef(pane.composerText);
  composerTextRef.current = pane.composerText;
  useEffect(() => {
    const handler = (e: Event) => {
      const transcript = (e as CustomEvent<string>).detail;
      if (!transcript) return;
      const base = composerTextRef.current.trim();
      pane.setComposerText(base ? `${base} ${transcript}` : transcript);
    };
    globalThis.addEventListener(MOO_V2_VOICE_COMMIT_EVENT, handler);
    return () =>
      globalThis.removeEventListener(MOO_V2_VOICE_COMMIT_EVENT, handler);
  }, [pane.setComposerText]);

  // Stop any in-progress recording when the user switches conversations or
  // agents so dictation can't bleed into the next thread.
  const prevConversationIdRef = useRef(pane.conversationId);
  const prevAgentIdRef = useRef(pane.agentId);
  useEffect(() => {
    const convChanged =
      prevConversationIdRef.current !== pane.conversationId &&
      prevConversationIdRef.current !== null;
    const agentChanged = prevAgentIdRef.current !== pane.agentId;
    prevConversationIdRef.current = pane.conversationId;
    prevAgentIdRef.current = pane.agentId;
    if (convChanged || agentChanged) {
      globalThis.dispatchEvent(new Event(MOO_V2_VOICE_CANCEL_EVENT));
    }
  }, [pane.conversationId, pane.agentId]);

  // Stop any in-progress recording when the panel closes. Inner stays
  // mounted across open/close (see the threat-actor/source-panel cleanup
  // effects below), so without this the mic and transcription WebSocket
  // would keep running with no visible mic control left to stop them.
  useEffect(() => {
    if (!launcher.isOpen) {
      globalThis.dispatchEvent(new Event(MOO_V2_VOICE_CANCEL_EVENT));
    }
  }, [launcher.isOpen]);

  // Auto-select the agents catalog's default once it lands. The library's
  // useAgentsCatalog (called by AgentsSection when the drawer opens) seeds
  // `agents.defaultAgentId` via dispatch on success.
  const { agentId: selectedAgentId, selectAgent } = selection;
  const { defaultAgentId } = agents;
  const serverDefaultAgentId = userPrefs?.defaultAgentId ?? null;
  const startupDefaultAgentId = serverDefaultAgentId ?? defaultAgentId;
  const selectedAgent = selectedAgentId
    ? agents.byId[selectedAgentId] ?? null
    : null;
  useEffect(() => {
    if (!storageHydrated) return;
    if (startupDefaultAgentId && !selectedAgentId) {
      selectAgent(startupDefaultAgentId);
    }
  }, [storageHydrated, startupDefaultAgentId, selectedAgentId, selectAgent]);

  // `limit_moo_agents` can hide the agent we just landed on. Storage hydration
  // already refuses to restore a persisted id that isn't in the catalog, but
  // the server-side prefs default (`userPrefs.defaultAgentId`) is adopted
  // unvalidated above, and the flag payload can change mid-session. Either way
  // the panel would be left on an agent the picker no longer lists, with the
  // header falling back to the literal "Moo" — so move to the catalog's own
  // default instead. Without an allow-list in effect this is exactly the
  // previous behaviour.
  const { status: agentsStatus, byId: agentsById } = agents;
  // Whether the allow-list actually narrowed anything — NOT merely whether the
  // payload is non-empty. The library fails open, so a list matching nothing
  // leaves the full catalog in place and the flag is inert; keying the clamp
  // on the payload instead would let a single mistyped id yank a Cowbell user
  // off an off-catalog custom agent (and reset their conversation) while the
  // picker is still showing every agent. When the catalog *was* narrowed every
  // id left in it is an allowed id, so one match proves the filter took.
  const allowListInEffect = !!allowedAgentIds?.some((id) => !!agentsById[id]);
  // A flag-hidden agent can still arrive as the prefs default or a restored
  // selection. Clamp only when the selection is one of those ids, so an
  // off-catalog custom agent is never touched.
  const selectionHidden =
    !!selectedAgentId && !!hiddenAgentIds?.includes(selectedAgentId);
  useEffect(() => {
    if (!(allowListInEffect || selectionHidden) || !storageHydrated) return;
    if (agentsStatus !== 'ready') return;
    if (!selectedAgentId || agentsById[selectedAgentId]) return;
    // The *catalog's* default, never `startupDefaultAgentId` — the prefs
    // default is itself a candidate for being filtered out, and re-selecting
    // it here would loop. `defaultAgentId` is the catalog's first entry, so
    // it is always in `byId` (or null, on an empty catalog).
    if (defaultAgentId) selectAgent(defaultAgentId);
  }, [
    allowListInEffect,
    selectionHidden,
    storageHydrated,
    agentsStatus,
    agentsById,
    selectedAgentId,
    defaultAgentId,
    selectAgent,
  ]);

  // Keep restricted-catalog personas (agency / policyholder / carrier) ON an
  // agent from their server catalog — V2 parity with the V1 provider clamp
  // (OMNI-484/485/588). The shell persists the selected agent in
  // sessionStorage and this integration stays mounted across an in-app
  // account jump, so a stale Cowbell agent id (or a prefs default from
  // another scope) can otherwise survive into a restricted session, where
  // the header would fall back to the literal "Moo". Once the catalog has
  // settled, an out-of-catalog selection resets to the catalog default
  // (AGENT_SELECTED also starts a fresh conversation); on a settled-empty
  // catalog (fetch error) it pins the persona's fail-safe agent rather than
  // leave a possibly-Cowbell selection. And even when the agent survives the
  // jump (in both catalogs, e.g. cyber-pulse on an agency -> policyholder
  // switch), the prior scope's conversation must not carry over — same rule
  // as V1's enteredRestricted clearing.
  const innerActor = useActor();
  const restrictedPersona = hasRestrictedCatalog(innerActor);
  const restrictedFailsafe = restrictedFailsafeAgentId(innerActor);
  const { conversationId: selectedConversationId, selectConversation } =
    selection;
  const wasRestrictedRef = useRef(restrictedPersona);
  useEffect(() => {
    const enteredRestricted = restrictedPersona && !wasRestrictedRef.current;
    wasRestrictedRef.current = restrictedPersona;
    if (!restrictedPersona || !storageHydrated) return;
    const clearCarriedConversation = () => {
      if (enteredRestricted && selectedConversationId !== null) {
        selectConversation(null);
      }
    };
    if (agents.status === 'ready') {
      if (!selectedAgentId) return; // default adoption above handles this
      if (!agents.byId[selectedAgentId]) {
        selectAgent(
          agents.defaultAgentId ?? agents.allIds[0] ?? restrictedFailsafe
        );
      } else {
        clearCarriedConversation();
      }
      return;
    }
    if (agents.status === 'error') {
      if (selectedAgentId !== restrictedFailsafe) {
        selectAgent(restrictedFailsafe);
      } else {
        clearCarriedConversation();
      }
    }
  }, [
    restrictedPersona,
    restrictedFailsafe,
    storageHydrated,
    agents.status,
    agents.byId,
    agents.allIds,
    agents.defaultAgentId,
    selectedAgentId,
    selectedConversationId,
    selectConversation,
    selectAgent,
  ]);

  // External trigger: top-bar Ask Moo button dispatches MOO_V2_TOGGLE_EVENT
  // when the V2 flag is active. We listen and toggle the launcher state.
  const { toggle } = launcher;
  useEffect(() => {
    const handler = () => toggle();
    globalThis.addEventListener(MOO_V2_TOGGLE_EVENT, handler);
    return () => globalThis.removeEventListener(MOO_V2_TOGGLE_EVENT, handler);
  }, [toggle]);

  // ⌘J / Ctrl+J toggles the panel from anywhere in the console.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isLauncherShortcut(e)) return;
      e.preventDefault();
      toggle();
    };
    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, [toggle]);

  // The top-bar button lives outside this tree; tell it whether the panel is
  // open and how many Moo tasks are running across every conversation.
  const totalRunning = Object.values(activeRunCounts).reduce(
    (sum, count) => sum + (count ?? 0),
    0
  );
  useEffect(() => {
    publishLauncherStatus({
      isOpen: launcher.isOpen,
      runningCount: totalRunning,
    });
  }, [launcher.isOpen, totalRunning]);

  // Proactive dwell nudge deep-link → open conversation + panel (window event).
  const { open: openLauncher } = launcher;
  const { openConversation } = selection;
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (
        e as CustomEvent<{ conversationId?: string; agentId?: string }>
      ).detail;
      if (!d?.conversationId) return;
      openConversation(d.conversationId, d.agentId ?? '');
      openLauncher();
    };
    globalThis.addEventListener(MOO_V2_OPEN_CONVERSATION_EVENT, handler);
    return () =>
      globalThis.removeEventListener(MOO_V2_OPEN_CONVERSATION_EVENT, handler);
  }, [openConversation, openLauncher]);

  // Returning from Moo Studio (OMNI-692). Studio renders outside ConsoleLayout,
  // so this panel isn't mounted while the user is there and can't be reached by
  // a window event; its "Back to Ask Moo" navigates back into the console and
  // leaves a one-shot flag for us to open on arrival. Consumed on read, so a
  // later remount doesn't reopen a panel the user has since closed.
  useEffect(() => {
    if (consumeAskMooOpenOnMount()) {
      openLauncher();
    }
  }, [openLauncher]);

  // Escape key closes the sidebar when it is open and the drawer is not.
  // Guard on !drawerIsOpen so pressing Escape during drawer navigation
  // (e.g. agent picker) closes the drawer first rather than the whole sidebar.
  const { isOpen: launcherIsOpen, close } = launcher;
  const { isOpen: drawerIsOpen } = drawer;
  useEffect(() => {
    if (!launcherIsOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !drawerIsOpen) {
        close();
      }
    };
    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, [launcherIsOpen, drawerIsOpen, close]);

  // Threat-actor panel state — opened via browser event bridged from the
  // config memo (which has no React state). The config dispatches
  // MOO_V2_OPEN_THREAT_ACTOR_EVENT; Inner listens and sets panel source.
  const [threatActorSource, setThreatActorSource] = useState<Source | null>(
    null
  );
  const closeThreatActor = useCallback(() => setThreatActorSource(null), []);

  // CVE panel state — same dedicated-event pattern as the threat-actor panel
  // above (not part of ActivePanel: the config memo dispatches
  // MOO_V2_OPEN_CVE_EVENT; Inner listens and sets panel source).
  const [cveSource, setCveSource] = useState<Source | null>(null);
  const closeCve = useCallback(() => setCveSource(null), []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Source>).detail;
      if (!detail) return;
      setActivePanel(null);
      setCveSource(null);
      setThreatActorSource(detail);
    };
    globalThis.addEventListener(MOO_V2_OPEN_THREAT_ACTOR_EVENT, handler);
    return () =>
      globalThis.removeEventListener(MOO_V2_OPEN_THREAT_ACTOR_EVENT, handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Source>).detail;
      if (!detail) return;
      setActivePanel(null);
      setThreatActorSource(null);
      setCveSource(detail);
    };
    globalThis.addEventListener(MOO_V2_OPEN_CVE_EVENT, handler);
    return () => globalThis.removeEventListener(MOO_V2_OPEN_CVE_EVENT, handler);
  }, []);

  // Dismiss the threat-actor/cve panels when the chat sidebar closes —
  // otherwise they stay modal (backdrop, focus trap, Esc handling) and dock
  // against a stale sidebar width after the sidebar is gone. Clearing the
  // source also prevents the panel from resurfacing when the sidebar is
  // reopened.
  useEffect(() => {
    if (!launcher.isOpen) {
      setThreatActorSource(null);
      setCveSource(null);
    }
  }, [launcher.isOpen]);

  // Active source panel state (PDF, Comparables, Findings, Policy, Cmail).
  // Opened via browser event bridged from the config memo (same pattern as
  // ThreatActorPanel above).
  const [activePanel, setActivePanel] = useState<ActivePanel | null>(null);
  const closeActivePanel = useCallback(() => {
    setActivePanel(null);
    stripMooPolicyParam(history);
  }, [history]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ActivePanel>).detail;
      if (!detail) return;
      setThreatActorSource(null);
      setCveSource(null);
      setActivePanel(detail);
    };
    globalThis.addEventListener(MOO_V2_OPEN_SOURCE_PANEL_EVENT, handler);
    return () =>
      globalThis.removeEventListener(MOO_V2_OPEN_SOURCE_PANEL_EVENT, handler);
  }, []);

  // Route through closeActivePanel (not a bare setActivePanel) so closing the
  // launcher also strips a lingering policy deep-link param — parity with V1's
  // shell-close → closeOverlay. Otherwise the param survives and a later reload
  // reopens Moo + the drawer.
  useEffect(() => {
    if (!launcher.isOpen) closeActivePanel();
  }, [launcher.isOpen, closeActivePanel]);

  // "Copy link" for a non-routable policy (P100/P250) yields a ``?mooPolicy=``
  // deep link. On load, open Moo + the policy drawer. Declared after the
  // launcher-close cleanup above so it wins the initial (closed) render.
  useEffect(() => {
    if (!initialPolicyDeepLink) return;
    openedViaDeepLinkRef.current = true;
    deepLinkLandingPathRef.current = history.location?.pathname ?? null;
    openLauncher();
    setThreatActorSource(null);
    setCveSource(null);
    setActivePanel({
      type: 'policy',
      source: buildMooPolicySource(initialPolicyDeepLink),
    });
    // Consume the deep link (clear provider state) so a later remount can't
    // re-run this empty-deps effect and reopen a drawer the user already
    // closed — the provider outlives this component. The URL param is
    // removed separately by closeActivePanel, which also fires on the initial
    // closed mount (the launcher-close effect above), so both recovery sources
    // are cleared eagerly; restoring the drawer after a refresh / remount is
    // intentionally unsupported (the landing page rewrites the query string).
    clearInitialPolicyDeepLink?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move the deep-linked policy drawer onto the policies page once scopes have
  // loaded. The copied link lands on the dashboard because a cold hard-load of
  // the permission-gated /policies route redirects there while scopes are empty
  // (see ProtectedRoute); client-side navigation after scopes load passes that
  // guard, and the drawer overlay stays open across the route change. Runs once.
  //
  // Scopes load async on a cold load, so this can fire a beat after mount. Only
  // relocate if the deep-linked drawer is still open AND the user hasn't
  // navigated away from the landing page in the meantime — otherwise a late
  // scope load would hijack whatever page they moved to.
  useEffect(() => {
    if (!openedViaDeepLinkRef.current || movedToPoliciesRef.current) return;
    if (activePanel?.type !== 'policy') return;
    if (!scopes?.length) return;
    if (history.location?.pathname !== deepLinkLandingPathRef.current) {
      // User navigated away before scopes loaded — abandon the relocation.
      movedToPoliciesRef.current = true;
      return;
    }
    const policiesPath = buildPoliciesListPath();
    movedToPoliciesRef.current = true;
    if (history.location?.pathname !== policiesPath) {
      history.replace(policiesPath);
    }
  }, [scopes, activePanel, history]);

  const isCurrentAgentDefault =
    selectedAgentId !== null && selectedAgentId === serverDefaultAgentId;

  const handlePinToggle = useCallback(() => {
    updatePrefs.mutate({
      defaultAgentId: isCurrentAgentDefault ? null : selectedAgentId ?? null,
    });
    if (isCurrentAgentDefault) {
      // Unpinning: useStorageHydration only persists `selection.agentId` to
      // storage when it *changes* — it never re-runs just because the server
      // preference did. Left alone, the sessionStorage entry from when the
      // user originally selected this agent survives untouched, and on the
      // next mount storage-hydration re-selects it before the (now-cleared)
      // server default ever gets a say — OMNI-670.
      storage?.setSelectedAgentId(null);
    }
  }, [isCurrentAgentDefault, selectedAgentId, updatePrefs, storage]);

  let runningCount = 0;
  if (selectedConversationId) {
    runningCount =
      activeRunCounts[normalizeConversationId(selectedConversationId)] ?? 0;
  }

  return (
    <>
      <Show when={!launcher.isOpen}>
        <MooChatFloatingIcon
          selectedAgent={selectedAgent as Agent | null}
          toggleLauncher={toggle}
        />
      </Show>

      <LayoutContainer
        isOpen={launcher.isOpen}
        sidebarStorageKey={SIDEBAR_STORAGE_KEY}
      >
        <AskMooHeader
          agentName={selectedAgent?.name ?? 'Moo'}
          agentAvatar={
            selectedAgent ? (
              <AgentAvatar
                agent={
                  selectedAgent as Pick<
                    V1Agent,
                    'id' | 'name' | 'icon' | 'color' | 'category'
                  >
                }
                size="sm"
              />
            ) : (
              <AgentAvatar agent={null} size="sm" />
            )
          }
          runBadge={
            <ActiveRunCountBadge count={runningCount} variant="running" />
          }
          isFullscreen={layout.mode === 'fullscreen'}
          isFloating={layout.mode === 'floating'}
          onBackToSidebar={() => layout.setMode('sidebar')}
          onOpenDrawer={drawer.open}
          isDrawerOpen={drawer.isOpen}
          pin={
            selectedAgentId === null
              ? null
              : {
                  isDefault: isCurrentAgentDefault,
                  isLoading: updatePrefs.isLoading,
                  onToggle: handlePinToggle,
                }
          }
          onNewChat={() => selection.newConversation()}
          onClose={launcher.close}
        />
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ConnectedAppsBridge>
            <MooChat />
          </ConnectedAppsBridge>
        </div>
      </LayoutContainer>
      <ThreatActorPanel
        isOpen={launcher.isOpen && !!threatActorSource}
        onClose={closeThreatActor}
        source={threatActorSource}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
      />
      <CvePanel
        isOpen={launcher.isOpen && !!cveSource}
        onClose={closeCve}
        source={cveSource}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
      />
      <PDFViewerPanel
        isOpen={launcher.isOpen && activePanel?.type === 'pdf'}
        onClose={closeActivePanel}
        source={activePanel?.type === 'pdf' ? activePanel.source : null}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
        defaultWidth={PDF_PANEL_DEFAULT_WIDTH_REM * rootFontPx()}
      />
      <FilePreviewPanel
        isOpen={launcher.isOpen && activePanel?.type === 'filePreview'}
        onClose={closeActivePanel}
        source={activePanel?.type === 'filePreview' ? activePanel.source : null}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
      />
      <JsonViewerPanel
        isOpen={launcher.isOpen && activePanel?.type === 'json'}
        onClose={closeActivePanel}
        data={activePanel?.type === 'json' ? activePanel.data : null}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
      />
      <ComparablesPanel
        isOpen={launcher.isOpen && activePanel?.type === 'comparables'}
        onClose={closeActivePanel}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
      >
        {activePanel?.type === 'comparables'
          ? renderComparablesSourceBody(activePanel.source)
          : null}
      </ComparablesPanel>
      <FindingsPanel
        isOpen={launcher.isOpen && activePanel?.type === 'findings'}
        onClose={closeActivePanel}
        chatWidth={layout.mode === 'sidebar' ? sidebarWidth : 0}
        accountName={
          activePanel?.type === 'findings'
            ? (activePanel.source.metadata as { accountName?: string })
                ?.accountName
            : undefined
        }
      >
        {activePanel?.type === 'findings'
          ? renderFindingsSourceBody(activePanel.source)
          : null}
      </FindingsPanel>
      {activePanel?.type === 'policy' && (
        <PolicyOverlayDrawer
          open={launcher.isOpen}
          source={activePanel.source}
          onClose={closeActivePanel}
        />
      )}
      {activePanel?.type === 'quote' && (
        <QuoteOverlayDrawer
          open={launcher.isOpen}
          source={activePanel.source}
          onClose={closeActivePanel}
        />
      )}
      {activePanel?.type === 'cmail' && (
        <CmailModal
          open={launcher.isOpen}
          msgId={
            (activePanel.source.metadata as { msgId?: string })?.msgId ?? null
          }
          onClose={closeActivePanel}
          onOpenInCmailbox={() => {
            closeActivePanel();
            history.push('/admin/cmail');
          }}
        />
      )}
    </>
  );
}
